import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Slide,
  Paper,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DocumentScanner as RecognizeIcon,
  Download as ExportIcon,
  Close as CloseIcon,
  TableChart as ExcelIcon,
  Code as XmlIcon,
  TextSnippet as TxtIcon,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useLanguage } from "context/LanguageContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const BulkActionsBar = ({
  selectedCount,
  onClearSelection,
  onDelete,
  onRecognize,
  onExportXml,
  onExportExcel,
  onExportRoda,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportSelect = (action) => {
    action();
    handleExportClose();
  };

  return (
    <Slide direction="up" in={selectedCount > 0} mountOnEnter unmountOnExit>
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          bottom: isMdDown ? 0 : 24,
          left: isMdDown ? 0 : "50%",
          right: isMdDown ? 0 : "auto",
          transform: isMdDown ? "none" : "translateX(-50%)",
          zIndex: 1200,
          display: "flex",
          flexDirection: isMdDown ? "column" : "row",
          alignItems: isMdDown ? "stretch" : "center",
          gap: isMdDown ? 1 : 1.5,
          px: isMdDown ? 1.5 : 1.75,
          py: isMdDown ? 1.25 : 1,
          borderRadius: isMdDown ? "12px 12px 0 0" : 2,
          backgroundColor: "background.paper",
          color: "text.primary",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
          minWidth: { xs: "100%", sm: "auto" },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          width={{ xs: "100%", md: "auto" }}
          flexShrink={0}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {t("selectedDocuments") || "Pasirinkti dokumentai"}: {selectedCount} (
            {t("onThisPage") || "šiame puslapyje"})
          </Typography>
          <IconButton size="small" onClick={onClearSelection} sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider
          orientation={isMdDown ? "horizontal" : "vertical"}
          flexItem
          sx={{ borderColor: "divider" }}
        />

        <Box
          display="grid"
          gridTemplateColumns={isMdDown ? "repeat(2, minmax(0, 1fr))" : "none"}
          gap={1}
          width={{ xs: "100%", md: "auto" }}
          sx={
            isMdDown
              ? {}
              : {
                  display: "flex",
                  flexWrap: "wrap",
                }
          }
        >
          <MDButton
            variant="contained"
            color="info"
            size="small"
            startIcon={<RecognizeIcon />}
            onClick={onRecognize}
            sx={{ width: isMdDown ? "100%" : "auto" }}
          >
            {t("recognizeSelected")}
          </MDButton>

          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<ExportIcon />}
            onClick={handleExportClick}
            sx={{ width: isMdDown ? "100%" : "auto" }}
          >
            {t("export")}
          </MDButton>

          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
          >
            <MenuItem onClick={() => handleExportSelect(() => onExportXml("agnum", false))}>
              <ListItemIcon>
                <XmlIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("exportToAgnum")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExportSelect(onExportExcel)}>
              <ListItemIcon>
                <ExcelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("exportExcel")}</ListItemText>
            </MenuItem>
            {onExportRoda && (
              <MenuItem onClick={() => handleExportSelect(onExportRoda)}>
                <ListItemIcon>
                  <TxtIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("exportRodaTxt") || "Export RODA TXT"}</ListItemText>
              </MenuItem>
            )}
          </Menu>

          <MDButton
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{ width: isMdDown ? "100%" : "auto" }}
          >
            {t("delete")}
          </MDButton>
        </Box>
      </Paper>
    </Slide>
  );
};

BulkActionsBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRecognize: PropTypes.func.isRequired,
  onExportXml: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
  onExportRoda: PropTypes.func,
};

export default BulkActionsBar;
